// -----------------------------------------------------------------------------
// CampaignDetailsPage.js — 2025‑05‑05 - Modern Responsive Update
// • ?creator=<username> deep‑link scrolling (unchanged)
// • NEW: "Launched‑only" simplifications:
//     –  removes Projections / Analytics tabs
//     –  hides view‑mode toggle when launched
// • Tabs/TabPanels stay 0‑indexed regardless of status
// • UPDATED: Modern responsive design with text expansion
// -----------------------------------------------------------------------------

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  useParams,
  useSearchParams,
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";
import { styled } from "@mui/material/styles";
import client from "../../../../API";
import { useMutation, useQuery } from "react-query";
import {
  Typography,
  Box,
  Divider,
  Toolbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Link,
  Avatar,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Collapse,
  Chip,
  Stack,
  Container,
  useTheme,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Download as DownloadIcon,
  Google as GoogleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import useAuth from "../../../../Hooks/use-auth";
import HomeHeader from "../../../../Components/HomeHeader";
import PageTitle from "../../../../Components/PageTitle";
import TabPanel from "../../../../Components/TabPanel";
import CampaignReportingPanel from "./campaignTabs/campaignReporting";
import CampaignDetailsPanel from "./campaignTabs/campaignDetails";
import CampaignProjectionsPanel from "./campaignTabs/campaignProjections";
import CampaignDetailsPanelLaunched from "./campaignTabs/launchedEditor";
import CRMDialog from "../../../Misc/crmComponents/crmPopup";
import RegisterPopup from "./campaignTabs/registerpopup";
import { formatLinksInMessage, parseLocalFloat } from "../../../../Utils/constants";

// ───────── styled helpers ─────────
const ModernGradientHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    pointerEvents: 'none',
  },
}));

const ModernToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px !important",
  padding: theme.spacing(1, 2),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  "&.Mui-selected": {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
  }
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  }
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 28,
}));

// helper CSS for row highlight
const injectHighlightCss = (() => {
  let done = false;
  return () => {
    if (done) return;
    const style = document.createElement("style");
    style.innerHTML = `
      .scroll-highlight {
        animation: blitzHighlight 3s ease-in-out forwards;
      }
      @keyframes blitzHighlight {
        0%   { background-color: rgba(255,235,59,0.7); }
        100% { background-color: transparent; }
      }
    `;
    document.head.appendChild(style);
    done = true;
  };
})();

// ────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ────────────────────────────────────────────────────────────────────────────
export default function CampaignDetailsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const sharedEmail   = searchParams.get("shared_email");
  const targetCreator = searchParams.get("creator");

  /* ─── local state ─── */
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [showInstructions, setShowInstructions] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [showCRMDialog, setShowCRMDialog] = useState(true);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [expandedTitle, setExpandedTitle] = useState(false);
  const [expandedBrief, setExpandedBrief] = useState(false);
  const hasShownMessage = useRef(false);

  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /* ─── fetch campaign ─── */
  const {
    data: campaignDetails,
    isLoading,
    refetch: refetchCampaign,
  } = useQuery(
    ["campaignDetails", campaignId],
    () => client.campaigns.fetch(campaignId),
    {
      onSuccess: (data) => {
        if (data.campaign_status === "Draft" && !hasShownMessage.current) {
          setShowInstructions(true);
          hasShownMessage.current = true;
        }
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  /* ─── register popup for shared links ─── */
  useEffect(() => {
    if (sharedEmail && !userInfo) setShowRegisterPopup(true);
  }, [sharedEmail, userInfo]);

  /* ─── default view (desktop=list, mobile=dashboard) ─── */
  useEffect(() => setViewMode(isDesktop ? "list" : "dashboard"), [isDesktop]);

  /* ─── mutations ─── */
  const { mutate: updateCreatorStats } = useMutation(
    (payload) => client.campaigns.updateCreatorStats(payload),
    {
      onSuccess: () => refetchCampaign(),
      onError: (err) =>
        alert(err?.response?.data?.error || err.message || "Update failed"),
    }
  );

  const {
    mutate: approveAssets,
    isLoading: isApproving,
  } = useMutation(
    ({ creatorId, approved }) =>
      client.campaigns.updateCreatorStats({
        campaignId,
        creators: [{ id: creatorId, assetsApproved: approved }],
      }),
    { onSuccess: () => refetchCampaign() }
  );

  const handleStatusChange = (creatorId, status) =>
    updateCreatorStats({ campaignId, creators: [{ id: creatorId, status }] });

  const handleNegotiateClick = () => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    navigate(`/editcampaign/${campaignId}`);
  };

  const handleChangeTab = (_, v) => setSelectedTab(v);
  const handleChangeView = (_, v) => v && setViewMode(v);

  /* ─── deep‑link scrolling ─── */
  const scrollToCreator = useCallback(() => {
    if (!targetCreator) return;
    const selector = `[data-creator-name="${targetCreator.toLowerCase()}"]`;
    const el =
      document.querySelector(selector) ||
      document.getElementById(`creator-${targetCreator.toLowerCase()}`);
    if (el) {
      injectHighlightCss();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("scroll-highlight");
      setTimeout(() => el.classList.remove("scroll-highlight"), 4000);
    }
  }, [targetCreator]);

  useEffect(() => {
    if (!campaignDetails) return;
    const t = setTimeout(scrollToCreator, 600);
    return () => clearTimeout(t);
  }, [campaignDetails, selectedTab, viewMode, scrollToCreator]);

  /* ─── text helpers ─── */
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const shouldShowExpand = (text, maxLength) => {
    return text && text.length > maxLength;
  };

  /* ─── loading / error states ─── */
  if (isLoading) {
    return (
      <Box sx={{ 
        width: "100vw", 
        height: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'white', mb: 2 }} size={60} />
          <Typography variant="h6" sx={{ color: 'white' }}>Loading campaign...</Typography>
        </Box>
      </Box>
    );
  }
  
  if (!campaignDetails) {
    return (
      <Box sx={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6">No campaign data found.</Typography>
      </Box>
    );
  }

  /* ─── status helpers ─── */
  const isLaunched = campaignDetails.campaign_status === "Launched";
  const isCampaignDraft = !["Launched", "Completed"].includes(campaignDetails.campaign_status);

  const getStatusChip = (status) => {
    const statusConfig = {
      "Draft": { color: "default", label: "Draft", icon: null },
      "Launched": { color: "success", label: "Live", icon: <LaunchIcon fontSize="small" /> },
      "Completed": { color: "primary", label: "Completed", icon: null }
    };
    
    const config = statusConfig[status] || statusConfig["Draft"];
    return (
      <StatusChip 
        label={config.label} 
        color={config.color}
        icon={config.icon}
        variant={status === 'Launched' ? 'filled' : 'outlined'}
      />
    );
  };

  // Text expansion logic
  const titleMaxLength = isMobile ? 30 : 50;
  const briefMaxLength = isMobile ? 120 : 200;
  
  const shouldExpandTitle = shouldShowExpand(campaignDetails.name, titleMaxLength);
  const shouldExpandBrief = shouldShowExpand(campaignDetails.brief, briefMaxLength);
  
  const displayTitle = expandedTitle || !shouldExpandTitle 
    ? campaignDetails.name 
    : truncateText(campaignDetails.name, titleMaxLength);
    
  const displayBrief = expandedBrief || !shouldExpandBrief 
    ? campaignDetails.brief 
    : truncateText(campaignDetails.brief, briefMaxLength);

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  RENDER                                                                  */
  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <HomeHeader />
      <Toolbar />
      <PageTitle title={`${campaignDetails.name} - Blitz`} />

      {/* ─── Modern Hero Section ─── */}
      <ModernGradientHeader>
        <Container maxWidth="xl">
          <Box sx={{ py: { xs: 4, md: 6 } }}>
            {/* Status and Actions Bar */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4,
              flexWrap: 'wrap',
              gap: 2
            }}>
              {/* Status Chip */}
              <Box>
                {getStatusChip(campaignDetails.campaign_status)}
              </Box>

              {/* Action Controls */}
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {!isLaunched && (
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleChangeView}
                    sx={{ 
                      bgcolor: "rgba(255,255,255,0.1)", 
                      borderRadius: 3,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    <ModernToggleButton value="list" size="small">
                      <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} /> 
                      {!isMobile && 'List View'}
                    </ModernToggleButton>
                    <ModernToggleButton value="dashboard" size="small">
                      <DashboardIcon sx={{ mr: 1, fontSize: 20 }} /> 
                      {!isMobile && 'Cards'}
                    </ModernToggleButton>
                  </ToggleButtonGroup>
                )}

                <ModernButton
                  variant="contained"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => {
                    if (!userInfo) { navigate("/login"); return; }
                    setExportMenuAnchor(e.currentTarget);
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.95)",
                    color: theme.palette.primary.main,
                    backdropFilter: 'blur(10px)',
                    "&:hover": { 
                      bgcolor: "rgba(255,255,255,1)",
                      transform: 'translateY(-2px)'
                    },
                  }}
                >
                  Export
                </ModernButton>
              </Stack>
            </Box>

            {/* Main Content */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'flex-start', lg: 'flex-end' },
              justifyContent: 'space-between',
              gap: 4
            }}>
              {/* Left: Campaign Info */}
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: { lg: '70%' } }}>
                {/* Campaign Title with Expand */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h2"
                      component="h1"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        color: "common.white",
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        lineHeight: 1.1,
                        wordBreak: 'break-word',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}
                    >
                      {displayTitle}
                    </Typography>
                    
                    {shouldExpandTitle && (
                      <Collapse in={expandedTitle}>
                        <Typography
                          variant="h2"
                          component="span"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            color: "common.white",
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            lineHeight: 1.1,
                            wordBreak: 'break-word',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                        >
                          {campaignDetails.name.substring(titleMaxLength)}
                        </Typography>
                      </Collapse>
                    )}
                  </Box>
                  
                  {shouldExpandTitle && (
                    <IconButton 
                      onClick={() => setExpandedTitle(!expandedTitle)}
                      sx={{ 
                        color: 'rgba(255,255,255,0.8)', 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': { 
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white'
                        }
                      }}
                      size="small"
                    >
                      {expandedTitle ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>

                {/* Campaign Brief with Expand */}
                {campaignDetails.brief && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          opacity: 0.95,
                          fontWeight: 400,
                          color: "common.white",
                          lineHeight: 1.6,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          wordBreak: 'break-word'
                        }}
                      >
                        {formatLinksInMessage(displayBrief)}
                      </Typography>
                      
                      {shouldExpandBrief && (
                        <Collapse in={expandedBrief}>
                          <Typography
                            variant="h6"
                            sx={{
                              opacity: 0.95,
                              fontWeight: 400,
                              color: "common.white",
                              lineHeight: 1.6,
                              fontSize: { xs: '1rem', md: '1.25rem' },
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              wordBreak: 'break-word'
                            }}
                          >
                            {formatLinksInMessage(campaignDetails.brief.substring(briefMaxLength))}
                          </Typography>
                        </Collapse>
                      )}
                    </Box>
                    
                    {shouldExpandBrief && (
                      <IconButton 
                        onClick={() => setExpandedBrief(!expandedBrief)}
                        sx={{ 
                          color: 'rgba(255,255,255,0.8)', 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': { 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                          }
                        }}
                        size="small"
                      >
                        {expandedBrief ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>

              {/* Right: Owner Info */}
              {campaignDetails.owner && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 3,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  p: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  minWidth: { lg: 280 }
                }}>
                  <Avatar 
                    src={campaignDetails.owner.phphref || ""} 
                    alt={`${campaignDetails.owner.first_name} ${campaignDetails.owner.last_name}`} 
                    sx={{ 
                      width: 64, 
                      height: 64,
                      border: '3px solid rgba(255,255,255,0.3)',
                      boxShadow: theme.shadows[4]
                    }} 
                  />
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: "rgba(255,255,255,0.8)", 
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px'
                    }}>
                      Presented By
                    </Typography>
                    <Link
                      component={RouterLink}
                      to={`/user/${campaignDetails.owner.id}`}
                      variant="h6"
                      underline="hover"
                      sx={{ 
                        color: "common.white",
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        '&:hover': {
                          color: 'rgba(255,255,255,0.9)'
                        }
                      }}
                    >
                      {campaignDetails.owner.first_name} {campaignDetails.owner.last_name}
                    </Link>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Container>

        {/* Export Menu */}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={() => setExportMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
              minWidth: 200,
              boxShadow: theme.shadows[8]
            }
          }}
        >
          <MenuItem
            onClick={() => {
              if (!campaignDetails) return;
              const csv = convertToCSV(campaignDetails);
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `campaign_${campaignId}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setExportMenuAnchor(null);
            }}
            sx={{ py: 1.5 }}
          >
            <DownloadIcon sx={{ mr: 2 }} /> CSV Export
          </MenuItem>
          <MenuItem
            onClick={async () => {
              if (!campaignDetails) return;
              try {
                const { sheetLink } = await client.campaigns.exportSheet({ campaignId, campaignDetails });
                sheetLink && window.open(sheetLink, "_blank");
              } catch (err) {
                console.error("Export failed:", err?.response?.data?.error || err.message);
              }
              setExportMenuAnchor(null);
            }}
            sx={{ py: 1.5 }}
          >
            <GoogleIcon sx={{ mr: 2 }} /> Google Sheets
          </MenuItem>
        </Menu>
      </ModernGradientHeader>

      {/* ─── Content Section ─── */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          {/* Modern Tabs */}
          <Tabs
            value={selectedTab}
            onChange={handleChangeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              mb: 4,
              "& .MuiTabs-indicator": { 
                height: 4, 
                borderRadius: 2,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              },
              "& .MuiTab-root": {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 60,
                px: 3
              }
            }}
          >
            <Tab 
              label="Campaign Overview" 
              icon={<AssignmentIcon />} 
              iconPosition="start"
            />
            {!isLaunched && <Tab label="Performance Projections" />}
            {!isLaunched && <Tab label="Creator Analytics" />}
          </Tabs>

          {/* Content Panels */}
          <Box sx={{ 
            bgcolor: "background.paper", 
            borderRadius: 3,
            boxShadow: theme.shadows[2],
            overflow: 'hidden'
          }}>
            {/* Overview (Tab 0) */}
            <TabPanel value={selectedTab} index={0}>
              {isLaunched ? (
                <CampaignDetailsPanelLaunched
                  campaignDetails={campaignDetails}
                  onApproveAssets={(id, creatorId, approved) => approveAssets({ creatorId, approved })}
                  isMutatingAssets={isApproving}
                />
              ) : (
                <CampaignDetailsPanel
                  campaignDetails={campaignDetails}
                  viewMode={viewMode}
                  isCampaignActive={isCampaignDraft}
                  handleStatusChange={handleStatusChange}
                  onNegotiateClick={handleNegotiateClick}
                />
              )}
            </TabPanel>

            {/* The following TabPanels are rendered only when NOT launched */}
            {!isLaunched && (
              <>
                <TabPanel value={selectedTab} index={1}>
                  <CampaignProjectionsPanel campaignDetails={campaignDetails} />
                </TabPanel>

                <TabPanel value={selectedTab} index={2}>
                  <CampaignReportingPanel
                    viewsByDayData={
                      campaignDetails.creators
                        ?.reduce((acc, c) => {
                          const d = c.postDate || new Date().toISOString();
                          const v = parseInt(c.totalViews || 0, 10);
                          const ex = acc.find((e) => e.date === d);
                          ex ? (ex.views += v) : acc.push({ date: d, views: v });
                          return acc;
                        }, [])
                        .sort((a, b) => new Date(a.date) - new Date(b.date)) || []
                    }
                    costPerCreatorData={
                      campaignDetails.creators?.map((c) => ({
                        name: c.name,
                        price: parseLocalFloat(String(c.price || 0).replace(/[^0-9.-]/g, "")),
                      })) || []
                    }
                    reachData={
                      campaignDetails.creators?.map((c) => ({
                        name: c.name,
                        value: parseInt(String(c.following || 0).replace(/,/g, ""), 10),
                      })) || []
                    }
                    creatorIds={campaignDetails.creators?.map((c) => c.id) || []}
                  />
                </TabPanel>
              </>
            )}
          </Box>
        </Box>
      </Container>

      {/* ─── Dialogs (updated styling) ─── */}
      <RegisterPopup 
        open={showRegisterPopup} 
        onClose={() => setShowRegisterPopup(false)} 
        initialEmail={sharedEmail} 
      />

      {showCRMDialog && (
        <CRMDialog
          isOpen={showCRMDialog}
          handleClose={() => setShowCRMDialog(false)}
          origin={`${campaignDetails.name} - campaign`}
        />
      )}

      <Dialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
            Welcome to Blitz 🚀
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ lineHeight: 1.6 }}>
            This sheet contains hand‑picked creators for your campaign. Approve assets, track progress, and export data in real time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 2 }}>
          <ModernButton onClick={() => setShowInstructions(false)} variant="contained">
            Let's Get Started!
          </ModernButton>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={loginDialogOpen} 
        onClose={() => setLoginDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 2 }
        }}
      >
        <DialogTitle>Please Login or Create an Account</DialogTitle>
        <DialogContent>
          <Typography>You must be logged in to perform this action.</Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button onClick={() => navigate("/login")} variant="outlined">
            Login
          </Button>
          <ModernButton onClick={() => navigate("/onboard")} variant="contained">
            Create an Account
          </ModernButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Utility — CSV converter (unchanged)
──────────────────────────────────────────────────────────────────────────── */
function convertToCSV(data) {
  if (!data?.creators?.length) return "";
  const headers = [
    "Name", "Following", "Status", "Price", "Offer Sent", "Assets Sent",
    "Assets Approved", "Promotion Type", "Promotion Platform", "Likes",
    "Comments", "Total Views", "Post Date", "Platform Link", "Live Link",
  ];
  const rows = data.creators.flatMap((creator) =>
    creator.videos.map((v) => [
      creator.name,
      creator.following,
      creator.status,
      creator.price,
      creator.offerSent ? "Yes" : "No",
      creator.assetsSent ? "Yes" : "No",
      creator.assetsApproved ? "Yes" : "No",
      v.promotionType,
      v.promotionPlatform,
      v.likes,
      v.comments,
      v.totalViews,
      v.postDate,
      v.platformLink,
      v.liveLink,
    ])
  );
  return [headers, ...rows].map((r) => r.join(",")).join("\n");
}